import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import React, { useState, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import TabsDataFilterHandler from "@sunSource/Store/Data/TabsData/Handler/TabsDataFilterHandler";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";

const enum fields {
    filterTitle = "filterTitle",
    video = "video",
    download = "download",
    blog = "blog",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.filterTitle]: string;
        [fields.video]: string;
        [fields.download]: string;
        [fields.blog]: string;
    };
}

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    return {
        filteredData: state.sunSourceState.TabsActiveData.filteredData,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    TabsDataFilterHandler,
};

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface TabDataFilterStyles {
    title: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    condensedViewCheckbox?: CheckboxPresentationProps;
}

export const tabDataFilterStyles: TabDataFilterStyles = {
    title: {
        css: css`
            color: ${getColor("primary")};
            text-transform: uppercase;
        `,
    },
    linkWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 10px;
        `,
    },
    condensedViewCheckbox: {
        typographyProps: {
            css: css`
                user-select: none;
                cursor: pointer;
            `,
        },
    },
};

const styles = tabDataFilterStyles;

const TabDataFilter: React.FC<Props> = props => {
    const { fields, filteredData, TabsDataFilterHandler, history, location } = props;
    const { filterTitle, video, download, blog } = fields;
    const [isVideoSelected, setIsVideoSelected] = useState(false);
    const [isDownloadSelected, setIsDownloadSelected] = useState(false);
    const [isBlogSelected, setIsBlogSelected] = useState(false);

    const updateFilterQueryString = (videoSelected: boolean, downloadSelected: boolean, blogSelected: boolean) => {
        const url = new URL(window.location.href);

        if (videoSelected) {
            url.searchParams.set("videoFilter", "1");
        } else {
            url.searchParams.delete("videoFilter");
        }

        if (downloadSelected) {
            url.searchParams.set("downloadFilter", "1");
        } else {
            url.searchParams.delete("downloadFilter");
        }

        if (blogSelected) {
            url.searchParams.set("blogFilter", "1");
        } else {
            url.searchParams.delete("blogFilter");
        }

        history.replace(url.pathname + url.search);
    };

    useEffect(() => {
        const url = new URL(window.location.href);
        const hasVideoFilter = url.searchParams.has("videoFilter");
        const hasDownloadFilter = url.searchParams.has("downloadFilter");
        const hasBlogFilter = url.searchParams.has("blogFilter");

        if (hasVideoFilter || hasDownloadFilter || hasBlogFilter) {
            const videoFromUrl =
                url.searchParams.get("videoFilter") === "1" || url.searchParams.get("videoFilter") === "true";
            const downloadFromUrl =
                url.searchParams.get("downloadFilter") === "1" ||
                url.searchParams.get("downloadFilter") === "true";
            const blogFromUrl =
                url.searchParams.get("blogFilter") === "1" || url.searchParams.get("blogFilter") === "true";

            setIsVideoSelected(videoFromUrl);
            setIsDownloadSelected(downloadFromUrl);
            setIsBlogSelected(blogFromUrl);
            TabsDataFilterHandler({ video: videoFromUrl, download: downloadFromUrl, blog: blogFromUrl });
            return;
        }

        if (filteredData.video || filteredData.download || filteredData.blog) {
            TabsDataFilterHandler({ video: false, download: false, blog: false });
        }

        setIsVideoSelected(false);
        setIsDownloadSelected(false);
        setIsBlogSelected(false);
    }, [location.search]);

    useEffect(() => {
        setIsVideoSelected(filteredData.video);
        setIsDownloadSelected(filteredData.download);
        setIsBlogSelected(filteredData.blog);
    }, [filteredData.video, filteredData.download, filteredData.blog]);
    
    
    return (
        <StyledWrapper>
            <Typography {...styles.title} variant="h2">
                {filterTitle}
            </Typography>
            <StyledWrapper {...styles.linkWrapper}>
                <StyledWrapper>
                    <CheckboxGroup>
                        <Checkbox
                            {...styles.condensedViewCheckbox}
                            uid="tabDataFilter_video"
                            checked={isVideoSelected}
                            onChange={(_, value) => {
                                setIsVideoSelected(value);
                                TabsDataFilterHandler({ video: value, download: isDownloadSelected, blog: isBlogSelected });
                                updateFilterQueryString(value, isDownloadSelected, isBlogSelected);
                            }}
                        >
                            {video}
                        </Checkbox>
                        <Checkbox
                            {...styles.condensedViewCheckbox}
                            uid="tabDataFilter_download"
                            checked={isDownloadSelected}
                            onChange={(_, value) => {
                                setIsDownloadSelected(value);
                                TabsDataFilterHandler({ video: isVideoSelected, download: value, blog: isBlogSelected });
                                updateFilterQueryString(isVideoSelected, value, isBlogSelected);
                            }}
                        >
                            {download}
                        </Checkbox>
                        <Checkbox
                            {...styles.condensedViewCheckbox}
                            uid="tabDataFilter_blog"
                            checked={isBlogSelected}
                            onChange={(_, value) => {
                                setIsBlogSelected(value);
                                TabsDataFilterHandler({ video: isVideoSelected, download: isDownloadSelected, blog: value });
                                updateFilterQueryString(isVideoSelected, isDownloadSelected, value);
                            }}
                        >
                            {blog}
                        </Checkbox>
                    </CheckboxGroup>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(TabDataFilter)),
    definition: {
        displayName: "Tab Data Filter",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.filterTitle,
                defaultValue: "Filter",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.video,
                defaultValue: "Video",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                sortOrder: 2,
            },
            {
                name: fields.download,
                defaultValue: "Download",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                sortOrder: 3,
            },
            {
                name: fields.blog,
                defaultValue: "Blog",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                sortOrder: 4,
            },
        ],
    },
};

export default widgetModule;
